import { createListenerMiddleware } from '@reduxjs/toolkit';
import { persistState } from 'app/store/persistState';
import { DELETE_QUERY } from 'app/store/exploreData/types.js';
import {
    TOGGLE_MAXIMIZED_PREVIEW,
    SELECT_WORKBENCH_DATASET_TAB,
    SELECT_WORKBENCH_PROJECT_TAB,
    SELECT_WORKBENCH_GROUP_TAB,
    TOGGLE_AUTORUN_PREVIEW
} from 'app/store/preferences/types';

import {
    SAVE_DATA_GRID_PANEL_COLUMN_WIDTH,
    SAVE_EXPLORER_DATA_GRID_COLUMN_WIDTH,
    SAVE_METADATA_GRID_COLUMN_WIDTH,
    SAVE_DATA_PROFILE_COLUMN_WIDTH
} from 'app/store/columnWidths/types';
import { INVALIDATE_DATASET } from 'app/store/datasetData/types.js';
import { CREATE_PROJECT, LOAD_PROJECT } from 'app/store/projects/types.js';
import {
    DELETE_PROJECT_SUCCESS_PROJECT_DELETED,
    UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED
} from 'app/store/__generated__/events';
import {
    SEND_MESSAGE,
    START_CONVERSATION,
    EXECUTE_SQL as EXECUTE_NOTEBOOK_SQL,
    DELETE_CONVERSATION,
    DELETE_MESSAGE,
    UPDATE_SQL
} from 'app/store/notebook/types.js';
import {
    CANCEL_QUERY,
    DELETE_EXPLORE_QUERY,
    GET_QUERY_RESULTSET
} from 'app/store/snowflakeQueries/types.js';
import { ADD_XML, REMOVE_XML, SET_NOTES } from 'app/store/inventory/types.js';

export const persistMiddleware = createListenerMiddleware();

const ActionTypes = [
    TOGGLE_MAXIMIZED_PREVIEW,
    TOGGLE_AUTORUN_PREVIEW,
    SELECT_WORKBENCH_DATASET_TAB,
    SELECT_WORKBENCH_PROJECT_TAB,
    SELECT_WORKBENCH_GROUP_TAB,
    GET_QUERY_RESULTSET.SUCCEEDED,
    GET_QUERY_RESULTSET.FAILED,
    CANCEL_QUERY.SUCCEEDED,
    DELETE_EXPLORE_QUERY,
    DELETE_QUERY,
    SAVE_DATA_GRID_PANEL_COLUMN_WIDTH,
    SAVE_EXPLORER_DATA_GRID_COLUMN_WIDTH,
    SAVE_METADATA_GRID_COLUMN_WIDTH,
    SAVE_DATA_PROFILE_COLUMN_WIDTH,
    INVALIDATE_DATASET,
    CREATE_PROJECT.SUCCEEDED,
    LOAD_PROJECT.SUCCEEDED,
    DELETE_PROJECT_SUCCESS_PROJECT_DELETED,
    UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED,
    SEND_MESSAGE.SUCCEEDED,
    START_CONVERSATION,
    EXECUTE_NOTEBOOK_SQL.STARTED,
    DELETE_CONVERSATION,
    DELETE_MESSAGE,
    UPDATE_SQL,
    ADD_XML,
    REMOVE_XML,
    SET_NOTES
];

persistMiddleware.startListening({
    predicate: ({ type }) => ActionTypes.includes(type),
    effect: (action, api) => {
        const state = api.getState();

        switch (action.type) {
            case TOGGLE_MAXIMIZED_PREVIEW:
            case TOGGLE_AUTORUN_PREVIEW:
            case SELECT_WORKBENCH_DATASET_TAB:
            case SELECT_WORKBENCH_PROJECT_TAB:
            case SELECT_WORKBENCH_GROUP_TAB:
                return persistState('preferences', state.preferences);
            case INVALIDATE_DATASET:
            case DELETE_QUERY:
            case GET_QUERY_RESULTSET.SUCCEEDED:
            case GET_QUERY_RESULTSET.FAILED:
            case DELETE_EXPLORE_QUERY: {
                const { byQueryId, byExploreQueryUuid } = state.snowflakeQueries;

                const queriesToSave = {};
                const queriesToLog = [];

                Object.values(byExploreQueryUuid).forEach((metadata) => {
                    const query = byQueryId[metadata.queryId];

                    if (query) {
                        queriesToSave[metadata.queryId] = { ...query };
                        queriesToLog.push(metadata);
                    }
                });

                return persistState(
                    'snowflakeQueries',
                    {
                        byExploreQueryUuid,
                        byQueryId: queriesToSave
                    },
                    {
                        message: 'Persisting data for explore queries',
                        queries: queriesToLog.map((q) => ({ ...q.meta, sql: q.sql }))
                    }
                );
            }
            case SAVE_DATA_PROFILE_COLUMN_WIDTH:
            case SAVE_EXPLORER_DATA_GRID_COLUMN_WIDTH:
            case SAVE_DATA_GRID_PANEL_COLUMN_WIDTH:
            case SAVE_METADATA_GRID_COLUMN_WIDTH:
                return persistState('columnWidths', state.columnWidths);
            case LOAD_PROJECT.SUCCEEDED:
            case CREATE_PROJECT.SUCCEEDED:
            case UPDATE_PROJECT_NAME_SUCCESS_PROJECT_NAME_UPDATED:
            case DELETE_PROJECT_SUCCESS_PROJECT_DELETED: {
                const { recentlyOpenedProjectsByUser } = state.projects;
                return persistState('projects', {
                    recentlyOpenedProjectsByUser
                });
            }
            case START_CONVERSATION:
            case SEND_MESSAGE.SUCCEEDED:
            case EXECUTE_NOTEBOOK_SQL.STARTED:
            case DELETE_CONVERSATION:
            case DELETE_MESSAGE:
            case UPDATE_SQL: {
                const { conversations } = state.notebook;
                return persistState('notebook', {
                    conversations
                });
            }
            case ADD_XML:
            case REMOVE_XML:
            case SET_NOTES: {
                const { xmls, notes } = state.inventory;

                return persistState('inventory', { xmls, notes });
            }
            default:
                break;
        }
    }
});
